import {
  BoolSelectFormsy,
  CheckboxesFormsy,
  CheckboxFormsy,
  ChipSelectFormsy,
  EuActivitySelectFormsy,
  DatePickerFormsy,
  FileUploadFormsy,
  FileUploadOnSelectFormsy,
  PhoneInputFormsy,
  RadioGroupFormsy,
  RemoteChipSelectFormsy,
  SelectFormsy,
  SliderFormsy,
  TextFieldFormsy,
  TextListFormsy,
  TabularSingleFormsy,
  TabularMultipleFormsy,
} from './formsy';
import { styled } from '@mui/material/styles';
import { AxiosResponse } from 'axios';
import InputAdornment from '@mui/material/InputAdornment';
import {
  CommodityCodeDto,
  ErrorQuestionDto,
  QuestionDateDto,
  QuestionDto,
  QuestionFileListDto,
  QuestionMultipleChoiceDto,
  QuestionNumberDto,
  QuestionResponseBoolDto,
  QuestionResponseDateDto,
  QuestionResponseDto,
  QuestionResponseMultipleChoiceDto,
  QuestionResponseNumberDto,
  QuestionResponseTextDto,
  QuestionResponseTextListDto,
  QuestionSubSectionListDto,
  QuestionTextDto,
  ScoringInformationDto,
  SectionResponseDto,
  SectionResponseUpdatedDto,
  SectionUpdateDto,
  SectorCodeDto,
  SubSectionDto,
  SubSectionResponseDto,
  QuestionTabularChoiceDto,
  QuestionResponseTabularChoiceDto,
} from 'models/dtoTypes';
import SubQuestionnaire from 'containers/SubQuestionnaire';
import SubQuestionnaireForDirectorOrShareholder from 'containers/SubQuestionnaireForDirectorOrShareholder';
import QuestionActionContainer from 'containers/QuestionActionContainer';
import { EWidget } from 'enums';
import { generateTextValidation } from 'utils/generateValidation';
import { SearchHsCode, SearchSectorCode } from 'services/openapiClient';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import Link from '@mui/material/Link';
import FaqModal from 'dialogs/faqModal';
import { useState } from 'react';
import faqs from 'assets/json/faq.json';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const PREFIX = 'Question';

const classes = {
  inputGroup: `${PREFIX}-inputGroup`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.inputGroup}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    '& > *': {
      width: '100%',
    },
  },
}));

interface Option {
  label?: string;
  alias?: string;
}

interface FormattedOption {
  label?: string;
  alias?: string;
}

interface OnSubmitSubSectionResponse {
  (
    questionAlias: string,
    questionResponses: QuestionResponseDto[],
  ): Promise<AxiosResponse<SectionResponseDto | ErrorQuestionDto> | void>;
}

interface OnDeleteSubSectionResponse {
  (subSuctionAlias: string, subSectionResponseId: string): void;
}

interface OnDeleteFile {
  (fileId: string): void;
}

interface OnGeneratePostFileData {
  (questionAlias: string, data: string, fileName: string, extension: string): SectionUpdateDto;
}

interface OnUpdateSection {
  (sectionResponse: SectionResponseUpdatedDto): void;
}

interface Props {
  disabled?: boolean;
  sectionAlias?: string;
  action?: ScoringInformationDto;
  responses?: QuestionResponseDto[];
  onDeleteSubSectionResponse?: OnDeleteSubSectionResponse;
  onGeneratePostFileData?: OnGeneratePostFileData;
  onUpdateSection?: OnUpdateSection;
  onDeleteFile?: OnDeleteFile;
  onSubmitSubSectionResponse?: OnSubmitSubSectionResponse;
  widget: EWidget;
  questionDto: QuestionDto;
  // sub section
  subSection?: SubSectionDto;
  subSectionResponses?: SubSectionResponseDto[];
}

function Question(props: Props): JSX.Element {
  const {
    action,
    disabled,
    onDeleteFile,
    onUpdateSection,
    onGeneratePostFileData,
    questionDto,
    responses,
    sectionAlias,
    subSection,
    subSectionResponses,
    widget,
  } = props;

  const { alias = '', description, label, readOnly, required } = questionDto;

  const [openFaqModalTabId, setOpenFaqModalTabId] = useState('');

  const formttedLabel = required ? `${label || ''} *` : label;

  const defaultSelectPlaceholder = 'Select...';

  const { t } = useTranslation('default');

  const getFirst = function <TQuestionType extends QuestionResponseDto>(
    array?: QuestionResponseDto[],
  ): TQuestionType {
    const item = array == null || array.length == 0 ? null : array[0];
    return item as TQuestionType;
  };

  // for select EWidget, need to generate a option list from questiondto;
  const getFormattedOpions = (): FormattedOption[] => {
    const { options = [] as Option[] } = questionDto as QuestionMultipleChoiceDto;
    const optionFormatter = (option: Option): FormattedOption => {
      return {
        alias: option?.alias,
        label: option?.label,
      };
    };
    return options.map(optionFormatter);
  };

  const getTabularRowOptions = (): FormattedOption[] => {
    const { rowOptions = [] as Option[] } = questionDto as QuestionTabularChoiceDto;
    const optionFormatter = (option: Option): FormattedOption => {
      return {
        alias: option?.alias,
        label: option?.label,
      };
    };
    return rowOptions.map(optionFormatter);
  };

  const getTabularColumnOptions = (): FormattedOption[] => {
    const { columnOptions = [] as Option[] } = questionDto as QuestionTabularChoiceDto;
    const optionFormatter = (option: Option): FormattedOption => {
      return {
        alias: option?.alias,
        label: option?.label,
      };
    };
    return columnOptions.map(optionFormatter);
  };

  const faqAliases = faqs.map((a) => {
    return a.id;
  });

  const showFaqLink = faqAliases.includes(alias);

  const commonProps = {
    name: alias,
    label: formttedLabel,
    description,
    disabled: disabled || readOnly === true,
    showFaqLink: showFaqLink,
  };

  const renderUserGuide = () => {
    if (commonProps.showFaqLink) {
      return (
        <div>
          <Link onClick={() => setOpenFaqModalTabId(commonProps.name)}>{t('learn-more')}</Link>
          <FaqModal
            openFaqModalTabId={openFaqModalTabId}
            onClose={() => setOpenFaqModalTabId('')}
          />
        </div>
      );
    } else {
      return null;
    }
  };

  const renderInput = () => {
    switch (widget) {
      case EWidget.TABULAR_SINGLE: {
        return (
          <TabularSingleFormsy
            {...commonProps}
            rowOptions={getTabularRowOptions()}
            columnOptions={getTabularColumnOptions()}
            value={getFirst<QuestionResponseTabularChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.TABULAR_MULTIPLE: {
        return (
          <TabularMultipleFormsy
            {...commonProps}
            rowOptions={getTabularRowOptions()}
            columnOptions={getTabularColumnOptions()}
            value={getFirst<QuestionResponseTabularChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.TEXT: {
        const { characterMax, characterMin, format, regex } = questionDto as QuestionTextDto;
        const rules = { characterMax, characterMin, format, regex };
        const validationRules = generateTextValidation(rules);
        return (
          <TextFieldFormsy
            {...commonProps}
            type="text"
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
            value={getFirst<QuestionResponseTextDto>(responses)?.value}
          />
        );
      }
      case EWidget.PHONE: {
        return (
          <PhoneInputFormsy
            {...commonProps}
            validations={{
              isPhone: function (values: FormData, value: string) {
                // allow blank
                return !value || value === '+' || isValidPhoneNumber(value);
              },
            }}
            validationErrors={{
              isPhone: 'Invalid phone number',
            }}
            value={getFirst<QuestionResponseTextDto>(responses)?.value}
          />
        );
      }
      case EWidget.NUMBER: {
        const { max, min } = questionDto as QuestionNumberDto;
        return (
          <TextFieldFormsy
            {...commonProps}
            type="number"
            validations={{
              lessThenMax: function (values: FormData, value?: number) {
                // allow max or value missing
                return max === undefined || value === undefined || value <= max;
              },
              greaterThenMin: function (values: FormData, value?: number) {
                // allow min missing
                return min === undefined || value === undefined || value >= min;
              },
            }}
            validationErrors={{
              lessThenMax: `Maximum value ${max || 0}`,
              greaterThenMin: `Mimimum value ${min || 0}`,
            }}
            value={getFirst<QuestionResponseNumberDto>(responses)?.value}
          />
        );
      }
      case EWidget.PERCENTAGE: {
        const { max, min } = questionDto as QuestionNumberDto;
        return (
          <TextFieldFormsy
            {...commonProps}
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: {
                min,
                max,
                step: '0.01',
              },
            }}
            value={getFirst<QuestionResponseNumberDto>(responses)?.value}
          />
        );
      }
      case EWidget.CURRENCY: {
        const { max, min } = questionDto as QuestionNumberDto;
        return (
          <TextFieldFormsy
            {...commonProps}
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { min, max },
            }}
            value={getFirst<QuestionResponseNumberDto>(responses)?.value}
          />
        );
      }
      case EWidget.DATE: {
        const { dateMin, dateMax } = questionDto as QuestionDateDto;
        return (
          <DatePickerFormsy
            {...commonProps}
            variant="outlined"
            dateMin={dateMin}
            dateMax={dateMax}
            value={getFirst<QuestionResponseDateDto>(responses)?.value}
          />
        );
      }
      case EWidget.SINGLE_SELECT: {
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <SelectFormsy
            {...commonProps}
            variant="outlined"
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
            optionAlias={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.optionAliases}
            options={getFormattedOpions()}
          />
        );
      }
      case EWidget.SINGLE_AUTOCOMPLETE: {
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <ChipSelectFormsy
            {...commonProps}
            textFieldProps={{
              label: formttedLabel,
              disabled: disabled || readOnly === true,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            variant="outlined"
            options={getFormattedOpions()}
            isMulti={false}
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.CLIMATE_MIGRATION_ACTIVITY: {
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <EuActivitySelectFormsy
            {...commonProps}
            textFieldProps={{
              label: formttedLabel,
              disabled: disabled || readOnly === true,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            variant="outlined"
            options={getFormattedOpions()}
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.CLIMATE_ADAPTATION_ACTIVITY: {
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <EuActivitySelectFormsy
            {...commonProps}
            textFieldProps={{
              label: formttedLabel,
              disabled: disabled || readOnly === true,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            variant="outlined"
            options={getFormattedOpions()}
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.MULTIPLE: {
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <ChipSelectFormsy
            {...commonProps}
            textFieldProps={{
              label: formttedLabel,
              disabled: disabled || readOnly === true,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            variant="outlined"
            options={getFormattedOpions()}
            isMulti
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.HS: {
        const { allowMultiple } = questionDto as QuestionMultipleChoiceDto;
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <RemoteChipSelectFormsy
            {...commonProps}
            onSearchOptions={SearchHsCode}
            optionRenderer={({ id, commodity }: CommodityCodeDto) => ({
              alias: id,
              label: `${commodity as string} (${(id || '').toUpperCase()})`,
            })}
            variant="outlined"
            isMulti={allowMultiple}
            value={(
              getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions || []
            ).map((option) => {
              return {
                alias: option.alias,
                label: option.label,
              };
            })}
          />
        );
      }
      case EWidget.ESG_TAXONOMY: {
        const { allowMultiple } = questionDto as QuestionMultipleChoiceDto;
        commonProps.label = formttedLabel || defaultSelectPlaceholder;
        return (
          <RemoteChipSelectFormsy
            {...commonProps}
            onSearchOptions={SearchSectorCode}
            optionRenderer={({ id, sector }: SectorCodeDto) => ({
              alias: id,
              label: `${sector as string}`,
            })}
            variant="outlined"
            isMulti={allowMultiple}
            value={(
              getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions || []
            ).map((option) => {
              return {
                alias: option.alias,
                label: option.label,
              };
            })}
          />
        );
      }
      case EWidget.RADIO: {
        return (
          <RadioGroupFormsy
            {...commonProps}
            options={getFormattedOpions()}
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          ></RadioGroupFormsy>
        );
      }
      case EWidget.CHECKBOXES: {
        return (
          <CheckboxesFormsy
            {...commonProps}
            options={getFormattedOpions()}
            value={getFirst<QuestionResponseMultipleChoiceDto>(responses)?.selectedOptions}
          />
        );
      }
      case EWidget.CHECKBOX: {
        return (
          <CheckboxFormsy
            {...commonProps}
            value={getFirst<QuestionResponseBoolDto>(responses)?.value}
          />
        );
      }
      case EWidget.BOOL_SELECT: {
        return (
          <BoolSelectFormsy
            {...commonProps}
            variant="outlined"
            value={getFirst<QuestionResponseBoolDto>(responses)?.value}
          />
        );
      }
      case EWidget.RANGE: {
        const step = 1; // TODO: should be defined in api
        const { max, min } = questionDto as QuestionNumberDto;
        return (
          <SliderFormsy
            {...commonProps}
            step={step || 1}
            max={max}
            min={min}
            value={getFirst<QuestionResponseNumberDto>(responses)?.value}
          />
        );
      }
      // selected files are not uploaded directly
      case EWidget.IN_FORM_FILES: {
        const {
          filesMin,
          filesMax,
          fileMaxSize,
          extensions = [],
        } = questionDto as QuestionFileListDto;
        return (
          <FileUploadFormsy
            {...commonProps}
            filesMin={filesMin}
            filesMax={filesMax}
            maxFileSize={fileMaxSize}
            extensions={extensions}
            existingFiles={responses}
            onDeleteFile={(fileId: string) => onDeleteFile && onDeleteFile(fileId)}
          />
        );
      }
      // selected files will be uploaded directly
      case EWidget.FILES: {
        const {
          filesMin,
          filesMax,
          fileMaxSize,
          extensions = [],
        } = questionDto as QuestionFileListDto;
        return (
          <FileUploadOnSelectFormsy
            {...commonProps}
            filesMin={filesMin}
            filesMax={filesMax}
            maxFileSize={fileMaxSize}
            extensions={extensions}
            existingFiles={responses}
            onDeleteFile={(fileId: string) => onDeleteFile && onDeleteFile(fileId)}
            onUpdateSection={onUpdateSection}
            onGeneratePostFileData={onGeneratePostFileData}
          />
        );
      }
      case EWidget.TEXT_LIST: {
        return (
          <TextListFormsy
            {...commonProps}
            value={getFirst<QuestionResponseTextListDto>(responses)?.values}
          />
        );
      }
      case EWidget.SUB_SECTION: {
        const { itemsMin, itemsMax } = questionDto as QuestionSubSectionListDto;
        return (
          <SubQuestionnaire
            {...commonProps}
            sectionAlias={sectionAlias}
            itemsMin={itemsMin}
            itemsMax={itemsMax}
            onSubmitSubSectionResponse={props.onSubmitSubSectionResponse}
            onDeleteSubSectionResponse={props.onDeleteSubSectionResponse}
            onUpdateSection={onUpdateSection}
            subSection={subSection}
            subSectionResponses={subSectionResponses}
            onDeleteFile={(fileId: string) => onDeleteFile && onDeleteFile(fileId)}
          />
        );
      }
      case EWidget.DIRECTOR_OR_SHAREHOLDER_INPUT: {
        const { itemsMin, itemsMax } = questionDto as QuestionSubSectionListDto;
        return (
          <SubQuestionnaireForDirectorOrShareholder
            {...commonProps}
            sectionAlias={sectionAlias}
            itemsMin={itemsMin}
            itemsMax={itemsMax}
            onSubmitSubSectionResponse={props.onSubmitSubSectionResponse}
            onDeleteSubSectionResponse={props.onDeleteSubSectionResponse}
            onUpdateSection={onUpdateSection}
            subSection={subSection}
            subSectionResponses={subSectionResponses}
            onDeleteFile={(fileId: string) => onDeleteFile && onDeleteFile(fileId)}
          />
        );
      }
      case EWidget.FUNC: {
        return <></>;
      }
      default: {
        return (
          <TextFieldFormsy
            type="text"
            name={alias}
            label={formttedLabel}
            value={`Question Type: ${widget} not implemented yet`}
            disabled={true}
          />
        );
      }
    }
  };

  const params = [
    t(
      'your-company-profile-is-determined-based-on-the-information-you-provide-under-the-following-terms',
    ),
    '',
    t(
      'you-are-required-to-exercise-due-diligence-in-ensuring-that-all-responses-are-complete-and-truthful-following-the-assignment-of-a-company-registration-profile-it-is-imperative-to-promptly-update-your-profile-should-any-of-the-provided-information-change-providing-outdated-or-inaccurate-information-may-lead-to-the-invalidation-of-registration-statuses-and-may-impact-compliance-reports',
    ),
    '',
    t(
      'all-registrants-are-obligated-to-represent-their-business-data-accurately-as-this-information-forms-the-basis-for-the-company-evaluation-this-entails-disclosing-any-details-that-could-potentially-affect-the-algorithms-assessment-of-your-registration-profile-if-there-is-any-doubt-regarding-the-materiality-of-information-err-on-the-side-of-disclosure-wherever-feasible-prior-to-submitting-information-into-the-system-you-must-conduct-a-thorough-investigation-to-ascertain-all-necessary-disclosures-omitting-material-information-could-invalidate-registration-profiles-and-adversely-affect-compliance-reports',
    ),
    '',
    t(
      'every-statement-fact-and-document-submitted-through-the-registration-process-must-be-complete-truthful-and-accurate-if-the-forms-are-completed-by-someone-else-on-your-behalf-you-must-review-them-for-accuracy-and-completeness-prior-to-submission-it-is-essential-to-thoroughly-read-and-understand-the-declaration-before-agreeing-to-it',
    ),
    '',
    t(
      'i-hereby-declare-that-all-information-provided-is-accurate-and-truthful-to-the-best-of-my-knowledge-and-belief-and-i-commit-to-immediately-updating-the-company-profile-regarding-any-changes-therein',
    ),
  ];

  const renderText = (para: string, index: number) => {
    return <p key={index}>{para}</p>;
  };

  const location: string = useLocation().pathname.toString();
  const isSmartGovernance = location.includes('smart_governances');

  return (
    <Root className={classes.inputGroup} id={alias}>
      {alias === 'i-agree' ? (
        <div>
          <h3>{t('terms-and-conditions')}</h3>
          {isSmartGovernance ? (
            <h4>{t('providing-smart-governance-information')}</h4>
          ) : (
            <h4>{t('providing-company-information')}</h4>
          )}
          {params.map(renderText)}
        </div>
      ) : (
        <></>
      )}

      {action && action.questionAlias !== 'esg-taxonomy-question-1' && (
        <QuestionActionContainer action={action} />
      )}

      {renderInput()}

      {renderUserGuide()}
    </Root>
  );
}

export default Question;
